import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  PriorityHigh as PriorityHighIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { format, isToday, isTomorrow, isThisWeek } from 'date-fns';
import TaskDialog from '../../components/Tasks/TaskDialog';
import TaskFilters from '../../components/Tasks/TaskFilters';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import ListPagination from '../../components/Common/ListPagination';
import { getAuthToken } from '../../lib/authToken';

// Types
interface Task {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  taskType: string;
  dueDate: string | null;
  startDate: string | null;
  completedDate: string | null;
  estimatedHours: number | null;
  actualHours: number | null;
  isRecurring: boolean;
  recurrencePattern: string | null;
  recurrenceInterval: number | null;
  reminderEnabled: boolean;
  reminderTime: string | null;
  tags: string[] | string;
  notes: string | null;
  case: {
    id: string;
    caseNumber: string;
    title: string;
  } | null;
  client: {
    id: string;
    clientNumber: string;
    name: string;
  } | null;
  assignedUser: {
    id: string;
    name: string;
    email: string;
  } | null;
  creator: {
    id: string;
    name: string;
    email: string;
  } | null;
  createdAt: string | null;
  updatedAt: string | null;
  /** Write-only fields used when creating/updating (API accepts flat ids). */
  caseId?: string | null;
  clientId?: string | null;
  assignedTo?: string | null;
}

interface TasksResponse {
  success: boolean;
  data: {
    tasks: Task[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

interface Filters {
  search: string;
  status: string;
  priority: string;
  taskType: string;
  assignedTo: string;
  caseId: string;
  clientId: string;
  dueDateFrom: string;
  dueDateTo: string;
  isOverdue: boolean;
}

const TasksPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTaskForMenu, setSelectedTaskForMenu] = useState<Task | null>(null);
  const [filters, setFilters] = useState<Filters>({
    search: '',
    status: '',
    priority: '',
    taskType: '',
    assignedTo: searchParams.get('assignedTo') || '',
    caseId: '',
    clientId: '',
    dueDateFrom: '',
    dueDateTo: '',
    isOverdue: false,
  });
  const [activeTab, setActiveTab] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    const fromUrl = searchParams.get('assignedTo') || '';
    setFilters((prev) =>
      prev.assignedTo === fromUrl ? prev : { ...prev, assignedTo: fromUrl }
    );
  }, [searchParams]);

  const queryClient = useQueryClient();

  // Fetch tasks
  const {
    data: tasksData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tasks', user?.tenantId, filters, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== '') {
          params.append(key, value.toString());
        }
      });
      params.set('page', String(page));
      params.set('limit', String(limit));

      const response = await fetch(`/api/tasks?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }

      return response.json() as Promise<TasksResponse>;
    },
  });

  // Fetch task statistics
  const { data: statsData } = useQuery({
    queryKey: ['taskStats', user?.tenantId],
    queryFn: async () => {
      const response = await fetch('/api/tasks/stats/overview', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch task statistics');
      }

      return response.json();
    },
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (taskData: Partial<Task>) => {
      const response = await fetch('/api/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(taskData),
      });

      if (!response.ok) {
        throw new Error('Failed to create task');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskStats'] });
      toast.success('Task created successfully');
      setDialogOpen(false);
    },
    onError: () => {
      toast.error('Failed to create task');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ taskId, taskData }: { taskId: string; taskData: Partial<Task> }) => {
      const response = await fetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(taskData),
      });

      if (!response.ok) {
        throw new Error('Failed to update task');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskStats'] });
      toast.success('Task updated successfully');
      setDialogOpen(false);
    },
    onError: () => {
      toast.error('Failed to update task');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const response = await fetch(`/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete task');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskStats'] });
      toast.success('Task deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete task');
    },
  });

  // Handlers
  const handleCreateTask = () => {
    setSelectedTask(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleViewTask = (task: Task) => {
    setSelectedTask(task);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleDelete = (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      deleteMutation.mutate(taskId);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, task: Task) => {
    setAnchorEl(event.currentTarget);
    setSelectedTaskForMenu(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTaskForMenu(null);
  };

  const handleSaveTask = async (taskData: Partial<Task>) => {
    if (dialogMode === 'create') {
      createMutation.mutate(taskData);
    } else if (dialogMode === 'edit' && selectedTask) {
      updateMutation.mutate({ taskId: selectedTask.id, taskData });
    }
  };

  const handleFilterChange = (newFilters: Partial<Filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      taskType: '',
      assignedTo: '',
      caseId: '',
      clientId: '',
      dueDateFrom: '',
      dueDateTo: '',
      isOverdue: false,
    });
  };

  // Utility functions
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'cancelled':
        return 'error';
      case 'on_hold':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'primary';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return <PriorityHighIcon color="error" />;
      case 'high':
        return <PriorityHighIcon color="warning" />;
      default:
        return <PriorityHighIcon color="action" />;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      if (isToday(date)) {
        return 'Today';
      }
      if (isTomorrow(date)) {
        return 'Tomorrow';
      }
      if (isThisWeek(date)) {
        return format(date, 'EEEE');
      }
      return format(date, 'MMM dd, yyyy');
    } catch (error) {
      return 'N/A';
    }
  };

  const getDueDateColor = (task: Task) => {
    if (!task.dueDate) return 'text.secondary';
    if (task.status === 'completed' || task.status === 'cancelled') return 'text.secondary';
    
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    
    if (dueDate < today) return 'error.main';
    if (isToday(dueDate) || isTomorrow(dueDate)) return 'warning.main';
    return 'text.primary';
  };

  const getTaskTypeIcon = (taskType: string) => {
    switch (taskType) {
      case 'client_meeting':
        return <PersonIcon />;
      case 'court_hearing':
        return <BusinessIcon />;
      case 'document_review':
        return <DescriptionIcon />;
      case 'assignment':
        return <AssignmentIcon />;
      default:
        return <AssignmentIcon />;
    }
  };

  // Mock data for fallback
  const mockTasks: Task[] = [
    {
      id: '1',
      title: 'Review client contract',
      description: 'Review and finalize the client contract for Smith case',
      status: 'pending',
      priority: 'high',
      taskType: 'document_review',
      dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
      startDate: null,
      completedDate: null,
      estimatedHours: 2.5,
      actualHours: null,
      isRecurring: false,
      recurrencePattern: null,
      recurrenceInterval: null,
      reminderEnabled: true,
      reminderTime: null,
      tags: ['contract', 'review'],
      notes: 'Client is waiting for this',
      case: {
        id: '1',
        caseNumber: 'CASE-001',
        title: 'Smith vs. Johnson',
      },
      client: {
        id: '1',
        clientNumber: 'CLI-001',
        name: 'John Smith',
      },
      assignedUser: {
        id: '1',
        name: 'Jane Doe',
        email: 'jane@example.com',
      },
      creator: {
        id: '1',
        name: 'Jane Doe',
        email: 'jane@example.com',
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ];

  const tasks = error ? [] : (tasksData?.data?.tasks || mockTasks);
  const tasksTotal = tasksData?.data?.pagination?.total || tasks.length;
  const stats = statsData?.data?.overview;

  return (
    <Box p={3}>
      {error && (
        <ListLoadErrorAlert
          resourceLabel="tasks"
          onRetry={() => refetch()}
        />
      )}
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Tasks
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTask}
        >
          Create Task
        </Button>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Tasks
                </Typography>
                <Typography variant="h4">
                  {stats.total || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.pending || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  In Progress
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {stats.inProgress || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Completed
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.completed || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters and Search */}
      <Box mb={3}>
        <Box display="flex" gap={2} alignItems="center" mb={2}>
          <TextField
            placeholder="Search tasks..."
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
            InputProps={{
              startAdornment: <SearchIcon color="action" />,
            }}
            size="small"
            sx={{ minWidth: 300 }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearFilters}
          >
            Clear
          </Button>
        </Box>

        {showFilters && (
          <TaskFilters
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        )}
      </Box>

      {/* Tasks Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Task</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Case/Client</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No tasks found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tasks.map((task) => (
                <TableRow key={task.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {task.title}
                      </Typography>
                      {task.description && (
                        <Typography variant="body2" color="textSecondary" noWrap>
                          {task.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.status.replace('_', ' ')}
                      color={getStatusColor(task.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getPriorityIcon(task.priority)}
                      <Chip
                        label={task.priority}
                        color={getPriorityColor(task.priority) as any}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getTaskTypeIcon(task.taskType)}
                      <Typography variant="body2">
                        {task.taskType.replace('_', ' ')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={getDueDateColor(task)}
                    >
                      {formatDate(task.dueDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {task.assignedUser ? (
                      <Typography variant="body2">
                        {task.assignedUser.name}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Unassigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      {task.case && (
                        <Typography variant="body2">
                          {task.case.caseNumber}
                        </Typography>
                      )}
                      {task.client && (
                        <Typography variant="body2" color="textSecondary">
                          {task.client.name}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, task)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ListPagination
        page={page}
        limit={limit}
        total={tasksTotal}
        onPageChange={setPage}
        onLimitChange={setLimit}
      />

      {/* Task Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedTaskForMenu) {
            handleViewTask(selectedTaskForMenu);
            handleMenuClose();
          }
        }}>
          <ViewIcon sx={{ mr: 1 }} />
          View
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedTaskForMenu) {
            handleEditTask(selectedTaskForMenu);
            handleMenuClose();
          }
        }}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedTaskForMenu) {
            handleDelete(selectedTaskForMenu.id);
            handleMenuClose();
          }
        }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Task Dialog */}
      <TaskDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        taskData={selectedTask}
        mode={dialogMode}
        onSave={handleSaveTask}
      />
    </Box>
  );
};

export default TasksPage; 